import { Controller } from "@hotwired/stimulus";

/**
 * Поиск по списку, живущему в Turbo-кадре: список сужается по мере ввода.
 *
 * Поле лежит снаружи кадра и потому не перерисовывается вместе со списком —
 * фокус и каретка остаются на месте. Фильтрует сервер: на второй странице
 * списка клиенту видно не всё.
 *
 * Пока кадр грузится, рядом с полем крутится спиннер, а в самом кадре —
 * плашка «Загружаем» от `lib/loading.js`. Счётчик найденного сервер кладёт
 * внутрь кадра (`[data-count-label]`): Turbo подменяет только содержимое
 * кадра, поэтому атрибут на самом кадре после перерисовки остался бы старым.
 */
export default class extends Controller {
  static targets = ["input", "busy", "count"];
  static values = {
    frame: String,
    param: { type: String, default: "q" },
    delay: { type: Number, default: 250 },
  };

  connect() {
    this.frame = document.getElementById(this.frameValue);
    this.finish = this.finish.bind(this);

    // Кадр перерисовывает себя сам, о конце загрузки узнаём от него.
    this.frame?.addEventListener("turbo:frame-load", this.finish);
    this.frame?.addEventListener("turbo:fetch-request-error", this.finish);
  }

  disconnect() {
    clearTimeout(this.timer);
    this.frame?.removeEventListener("turbo:frame-load", this.finish);
    this.frame?.removeEventListener("turbo:fetch-request-error", this.finish);
  }

  /** Запрос уходит не на каждую букву, иначе на слово из шести их шесть. */
  filter() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.reload(), this.delayValue);
  }

  /** Enter перезагружать страницу не должен: список уже обновлён. */
  submit(event) {
    event.preventDefault();
    clearTimeout(this.timer);
    this.reload();
  }

  /** Перерисовывает кадр по адресу с запросом и правит адресную строку. */
  reload() {
    const frame = this.frame ?? document.getElementById(this.frameValue);
    if (!frame) return;

    const url = new URL(window.location.href);
    const query = this.inputTarget.value.trim();
    if (query) {
      url.searchParams.set(this.paramValue, query);
    } else {
      url.searchParams.delete(this.paramValue);
    }
    // Сужение списка всегда возвращает к первой странице.
    url.searchParams.delete("page");

    this.setBusy(true);
    frame.src = url.toString();
    window.history.replaceState({}, "", url);
  }

  /** Кадр загрузился: спиннер гасим, счётчик берём из ответа сервера. */
  finish() {
    this.setBusy(false);

    const label = this.frame?.querySelector("[data-count-label]")?.dataset.countLabel;
    if (this.hasCountTarget && label) this.countTarget.textContent = label;
  }

  setBusy(busy) {
    if (this.hasBusyTarget) this.busyTarget.hidden = !busy;
  }
}
